/*
 * Диалог «Прогон мониторинга» (docs/release-2026-09-19.md §3.6). Своих цветов нет ни одного —
 * только токены `tokens.css` (docs/ui-design.md §9). Таблица — глобальные `.tblwrap/.tbl` из
 * base.css с плотностью своей: у рабочей таблицы на полсотни строк паддинг 14/16 съел бы
 * колонку «Кратко» целиком.
 *
 * СПЕЦИФИЧНОСТЬ. base.css подключается ПОСЛЕ модулей (main.tsx импортирует App раньше
 * стилей), поэтому всё, что спорит с его `.tbl td` (0,1,1) и `.btn` (0,1,0), объявлено
 * составным селектором: `.table .td` (0,2,0), `:global(.btn).outline` (0,2,0).
 */

/* ── шапка диалога ───────────────────────────────────────────────────────── */

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.lead {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

/* Мета прогона: время, вид, «оценено N, релевантных M». Цифры табличные — строка не
   дёргается по ширине, когда таблица переходит на другой прогон. */
.meta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--ink-2);
  font-variant-numeric: tabular-nums;
  white-space: pre-wrap;
}

.controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}

.select {
  font-family: var(--sans);
  font-size: 13px;
  color: var(--ink);
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--r-btn);
  padding: 6px 12px;
  font-variant-numeric: tabular-nums;
  max-width: 100%;
  cursor: pointer;
}
.select:focus-visible {
  outline: 2px solid var(--violet-bg-2);
}

/* ⓘ у действия: кружок без рамки — вес пятна меньше любой кнопки рядом. */
.info {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex: none;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--muted);
  cursor: help;
}
.info:hover,
.info:focus-visible {
  color: var(--accent-ink);
  background: var(--violet-bg);
  outline: none;
}

/* Обводка «как кнопка в шапке»: белая, 1px `--violet`, текст `--violet`. Ей же нарисована
   «Пост готов» — действие, которое ведёт, а не заказывает, поэтому без заливки. */
:global(.btn).outline {
  background: var(--white);
  border-color: var(--violet);
  color: var(--accent-ink);
}
:global(.btn).outline:hover {
  background: var(--violet-bg);
  color: var(--accent-ink);
}
:global(.btn).outline:disabled {
  background: var(--white);
  border-color: var(--violet-bg-2);
  color: var(--muted);
  cursor: default;
}

/* Дыхание — то же, что у точки хода прогона (RunStatusBar.module.css): медленное, неполное. */
.dot {
  width: 7px;
  height: 7px;
  flex: none;
  border-radius: 50%;
  background: var(--violet);
  animation: breathe 1.8s var(--ease) infinite;
}

@keyframes breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

/* ── состояния ───────────────────────────────────────────────────────────── */

.state {
  font-size: 13px;
  color: var(--muted);
  padding: 24px 0;
  text-align: center;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 40px 16px;
  text-align: center;
  border: 1px dashed var(--border);
  border-radius: var(--r-card);
}

.emptyTitle {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
}

.grow {
  flex: 1;
  min-width: 0;
}

/* ── таблица ─────────────────────────────────────────────────────────────── */

/*
 * Раскладка фиксированная: ширины колонок данных заданы, «Новость» и «Кратко» делят остаток
 * поровну. `min-width` — пол, ниже которого (планшет, 641–1180px) таблица не сжимается, а
 * уезжает вбок внутри своего `.tblwrap` — тот же приём, что у широких таблиц «Мониторинга».
 * На телефоне пол снят: там таблица — карточки (блок внизу файла).
 */
.table {
  table-layout: fixed;
  min-width: 880px;
}

.colCheck {
  width: 40px;
}
.colSource {
  width: 120px;
}
.colDate {
  width: 116px;
}
.colRating {
  width: 104px;
}
.colStatus {
  width: 156px;
}

.table .th {
  padding: 10px;
}

.table .td {
  padding: 12px 10px;
  font-size: 13px;
  vertical-align: middle;
}

.row:hover .td {
  background: var(--bg-alt);
}

.check {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--violet);
  cursor: pointer;
  vertical-align: middle;
}

.titleLine {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

/* Две строки с многоточием; полный заголовок — в `title` узла. */
.title {
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.ext {
  display: inline-flex;
  flex: none;
  margin-top: 2px;
  color: var(--muted);
}
.ext:hover {
  color: var(--accent-ink);
}

.gist {
  font-size: 13px;
  line-height: 1.4;
  color: var(--muted);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.nil {
  color: var(--muted);
}

.source {
  display: block;
  color: var(--ink-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.date {
  color: var(--ink-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* Якорь подсказки, который выглядит как текст: кнопка — ради клавиатуры, а не ради вида. */
.plain {
  font: inherit;
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  text-align: left;
  cursor: help;
  border-radius: 4px;
}
.plain:focus-visible {
  outline: 2px solid var(--violet-bg-2);
  outline-offset: 2px;
}

.warn {
  display: inline-flex;
  color: var(--red-ink);
}

.rating {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ratingNum {
  min-width: 26px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

/* Полоска оценки: 40px, дорожка `--violet-bg`, заливка `--violet` (§3.6). */
.meter {
  display: inline-block;
  width: 40px;
  height: 6px;
  flex: none;
  border-radius: var(--r-pill);
  background: var(--violet-bg);
  overflow: hidden;
}

.meterFill {
  display: block;
  height: 100%;
  border-radius: var(--r-pill);
  background: var(--violet);
}

/* Нерелевантные строки приглушены — данные, но не действия: ☐ и кнопка статуса остаются
   в полную силу, иначе приглушённая кнопка читалась бы выключенной. */
.dim .cTitle,
.dim .cGist,
.dim .cSource,
.dim .cDate,
.dim .cRating {
  opacity: 0.5;
}

.status {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

/* Красная точка у «Написать»: прошлая попытка сорвалась, причина — в подсказке. Кнопка
   16px, а видимая точка 8px — цель для пальца и клавиатуры больше самой отметки.
   В таблице точка стоит в поле ячейки СЛЕВА от кнопки и места в ряду не занимает: иначе
   «Написать» у сорвавшейся строки съезжал бы вправо от колонки остальных кнопок. */
.failDot {
  position: absolute;
  left: -18px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex: none;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: help;
}
.failDot::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--red);
}
.failDot:focus-visible {
  outline: 2px solid var(--violet-bg-2);
}

.skip {
  white-space: nowrap;
}

/* Заголовок колонки, по которому сортируют: кнопка, одетая в стиль заголовка. */
.sortBtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.sortBtn:hover,
.sortOn {
  color: var(--ink);
}
.sortBtn:focus-visible {
  outline: 2px solid var(--violet-bg-2);
  outline-offset: 2px;
}

.arrow {
  font-size: 11px;
  opacity: 0.45;
}
.sortOn .arrow {
  opacity: 1;
  color: var(--accent-ink);
}

/* ── полоса выбора ───────────────────────────────────────────────────────── */

/* Закреплена у нижнего края карточки диалога: выбор делают, листая таблицу, и действие
   обязано оставаться под рукой, а не в конце полусотни строк. */
.selection {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 10px 18px;
  background: var(--white);
  border: 1px solid var(--violet-bg-2);
  border-radius: var(--r-btn);
  box-shadow: var(--sh-float);
}

.count {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.spacer {
  flex: 1;
}

/* ── подсказка ───────────────────────────────────────────────────────────── */

/* Геометрия `.tip` из WriterModelSwitch.module.css; положение — у окна (см. шапку TSX). */
.tip {
  position: fixed;
  z-index: 60;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: var(--r-ctl);
  background: var(--white);
  border: 1px solid var(--border);
  box-shadow: var(--sh-float);
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--ink-2);
  text-align: left;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
  overflow-wrap: anywhere;
  pointer-events: none;
}
.tip b {
  font-size: 13px;
  color: var(--ink);
}
.tipNote {
  color: var(--muted);
}

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Вход со строки «Прогоны мониторинга»: момент прогона, одетый ссылкой. Шрифт и цифры —
   ячейки, в которой он стоит; отличает его только цвет действия. */
.opener {
  font: inherit;
  font-variant-numeric: tabular-nums;
  color: var(--accent-ink);
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-align: left;
  border-radius: 4px;
}
.opener:hover {
  color: var(--accent-ink-strong);
  text-decoration: underline;
}
.opener:focus-visible {
  outline: 2px solid var(--violet-bg-2);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .dot {
    animation: none;
  }
}

/* ── телефон (≤640) — docs/ui-design.md §4.1 ─────────────────────────────────
 *
 * Строки становятся карточками: заголовок, суть, мета-строка «источник · дата · оценка» и
 * кнопка статуса во всю ширину (§3.6). Делается раскладкой, а не второй разметкой: те же
 * ячейки, три из них встают в строку. Второй экземпляр текста ради телефона удвоил бы
 * каждую строку для чтения с экрана.
 *
 * От шапки таблицы остаются только сортируемые заголовки — лентой кнопок над карточками.
 * Цена: часть браузеров снимает табличную семантику с элементов, чей `display` переопределён;
 * это осознанно — телефон здесь аварийный доступ (docs/ui-design.md §4.1), а не рабочее место.
 */
@media (max-width: 640px) {
  .toolbar {
    align-items: stretch;
  }
  .controls {
    width: 100%;
  }
  .select {
    flex: 1 1 auto;
    min-width: 0;
  }

  .table {
    min-width: 0;
  }
  .table,
  .table tbody,
  .table thead {
    display: block;
  }
  .table colgroup {
    display: none;
  }
  .table thead tr {
    display: flex;
    gap: 16px;
    padding: 8px 12px;
    background: var(--bg-alt);
    border-bottom: 1px solid var(--border);
  }
  .table .th {
    display: none;
    padding: 0;
    border: none;
    background: none;
  }
  .table .sortable {
    display: block;
  }
  .table tbody tr {
    display: block;
    position: relative;
    padding: 12px 12px 12px 42px;
    border-bottom: 1px solid var(--border-2);
  }
  .table tbody tr:last-child {
    border-bottom: none;
  }
  .table .td {
    display: block;
    padding: 0;
    border: none;
    background: none;
  }
  .table .cCheck {
    position: absolute;
    left: 14px;
    top: 14px;
  }
  .table .cGist {
    margin-top: 4px;
  }
  .table .cSource,
  .table .cDate,
  .table .cRating {
    display: inline;
    font-size: 12px;
  }
  /* Разделитель едет вместе со своим значением: ячейка даты и ячейка оценки неразрывны вместе с
     точкой перед значением, а единственное место переноса — пробел ПОСЛЕ предыдущей ячейки
     (`::after` с `white-space: normal`). Так строка ломается перед «·», и точка не повисает в
     конце строки. Неразрывный пробел после точки сам по себе не держит: Chrome переносит между
     ним и inline-flex оценки. До 19.09.2026 вместе с nowrap имени источника у строки не было
     ни одного места переноса, и длинное имя («Google — The Keyword (весь блог)») выдавливало
     оценку за край карточки. */
  .table .cDate::before,
  .table .cRating::before {
    content: "·\00a0";
    color: var(--muted);
  }
  .table .cSource::after,
  .table .cDate::after {
    content: " ";
    white-space: normal;
  }
  .table .cDate,
  .table .cRating {
    white-space: nowrap;
  }
  /* На карточке точка — в потоке, рядом с кнопкой: поля ячейки слева здесь нет. */
  .failDot {
    position: static;
    transform: none;
  }
  /* На карточке имя источника не обрезается многоточием, а переносится по словам. */
  .table .cSource .source {
    display: inline;
    white-space: normal;
  }
  .table .cStatus {
    margin-top: 10px;
  }
  .status > :global(.btn) {
    flex: 1 1 auto;
  }

  .selection {
    flex-wrap: wrap;
    border-radius: var(--r-card);
  }
}
